<template>
  <div max-w-lg>
    <div class="adthrive-header min-h-[280px] overflow-hidden"></div>
    <Ad mb-4 ad-slot="6966721074" />
    <div pb-5>
      <h2 font-slab text-lg sm:text-xl text-brand-blue>
        <NuxtLink to="/county-map" class="hover:text-blue-300">United States County Maps</NuxtLink>
      </h2>
      <p text-stone-500>Find US county maps for each State.</p>
    </div>
    <h1 text-3xl sm:text-4xl font-semibold text-stone-500>{{ title }}</h1>
    <div py-4 pt-10 space-y-10>
      <p>
        Find your {{ state.name }} {{ countyType.one.toLowerCase() }} by searching for your address, or by clicking on
        the map.
      </p>
    </div>
    <div py-10 space-y-10>
      <div w-full h-lg bg-stone-50>
        <LazyMapAddress
          field="countySubdivision.county.name"
          :bbox="state.bbox"
          :filter="['==', 'STATEFP', state.id]"
          @click="handleClick"
        />
      </div>
      <h2 text-xl sm:text-2xl font-semibold text-stone-500>{{ state.name }} {{ countyType.many }}</h2>
      <div>
        <!-- <ul flex flex-wrap gap-5 sm:gap-3>
          <li v-for="county in stateCounties" :key="county.id">
            <NuxtLink
              :to="`/county-map/${route.params.state}/${encodeUrlSlug(county.name)}`"
              class="hover:text-stone-400"
            >
              {{ county.name }}
            </NuxtLink>
          </li>
        </ul> -->
        <StateCountiesList :state="route.params.state" :id="state.id" flex flex-wrap gap-5 sm:gap-3 />
      </div>
      <p>
        <Button color="blue" icon="i-material-symbols-my-location-rounded" to="/what-county-am-i-in">
          Locate my {{ state.name }} {{ countyType.one.toLowerCase() }}
        </Button>
      </p>
      <div v-if="state.countySubdivisions?.length" space-y-4>
        <h2 text-2xl font-semibold text-stone-500>{{ state.name }} {{ state.countySubdivisions[0].many }} Map</h2>
        <div w-full h-lg bg-stone-50>
          <LazyMapAddress
            field="countySubdivision.fullName"
            :bbox="state.bbox"
            :filter="['all', ['==', 'STATEFP', state.id]]"
          />
        </div>
        <p>
          An interactive map of {{ countyType.one.toLowerCase() }} subdivisions in {{ state.name }}. In
          {{ state.name }}, those subdivisions are
          {{
            arrayToTextList(
              state.countySubdivisions.map((s) => s.many),
              "or"
            )
          }}.
          <NuxtLink :to="countySubdivisionLink(state)" class="font-bold hover:text-stone-400"
            >Find what {{ state.countySubdivisions[0].one.toLowerCase() }} you are in now.</NuxtLink
          >
        </p>
        <p>
          <Button color="blue" icon="i-material-symbols-my-location-rounded" :to="countySubdivisionLink(state)">
            Locate my {{ state.name }} {{ state.countySubdivisions[0].one.toLowerCase() }}
          </Button>
        </p>
      </div>

      <WikipediaInfo :slug="stateWiki" />
      <h2 text-xl sm:text-2xl font-semibold text-stone-500>What is this?</h2>
      <p>
        This site has an interactive map of {{ state.name }} {{ countyType.many }}. Click on the map to find your
        {{ state.name }} county, and view more details. You can also search for your address and use your GPS to find
        your {{ state.name }} {{ countyType.one }}.
      </p>
      <p>
        Find what {{ countyType.one }} you are in, and see official {{ state.name }} {{ countyType.one }} resources and
        information. Find any county in the United Stated, along with resources and links to official
        {{ countyType.one }} websites. See a {{ countyType.one }} map and local details. Looking for what county you are
        currently in? Find out on this map or
        <NuxtLink to="/what-county-am-i-in" font-semibold hover:text-stone-400
          >use your device GPS to see what county you are in</NuxtLink
        >.
      </p>
      <MoreResourcesLinks :center="center" :urlName="state.name" :displayName="state.name" />
    </div>
  </div>
</template>
<script setup>
import states from "~~/data/states.json";

const route = useRoute();
const router = useRouter();

function handleClick(features) {
  const county = features[0]?.properties?.NAME;
  if (county) {
    router.push(`/county-map/${route.params.state}/${encodeUrlSlug(county)}`);
  }
}

const state = computed(() => {
  const stateName = decodeUrlSlug(route.params.state);
  const state = Object.values(states).find((s) => s.name.toLowerCase() === stateName.toLowerCase());
  if (!state) {
    throw createError({ statusCode: 404, statusMessage: "Page Not Found" });
  }
  return state;
});

const center = computed(() => {
  // convert bbox to center
  const bbox = state.value.bbox;
  return {
    longitude: round((bbox[0] + bbox[2]) / 2, 2),
    latitude: round((bbox[1] + bbox[3]) / 2, 2),
  };
});

const countyType = computed(() => {
  if (state.value?.countyType) {
    return state.value.countyType;
  }
  return {
    one: "County",
    many: "Counties",
  };
});

const stateWiki = computed(() => {
  let article = state.value.name.replace(/\s/g, "_");
  if (["New_York", "Washington", "Georgia"].includes(article)) {
    article += "_(U.S._state)";
  }
  return article;
});

const title = computed(() => {
  return `${state.value.name} ${countyType.value.one} Map`;
});

const description = computed(() => {
  return `View a map of ${state.value.name} ${countyType.value.many}, find what ${state.value.name} ${countyType.value.one} you are in, and see official ${state.value.name} ${countyType.value.one} resources and information, and more.`;
});

const imageSubtitle = computed(() => {
  let subtitle = `Maps of ${state.value.name} by ${countyType.value.one.toLowerCase()}`;
  if (state.value?.countySubdivisions) {
    subtitle += ` and ${state.value.countySubdivisions[0].one.toLowerCase()}`;
  }
  return subtitle;
});

useSeoMeta({
  title: () => `${title.value} - What ${countyType.value.one.toLowerCase()} am I in?`,
  ogTitle: () => `${title.value} - What ${countyType.value.one.toLowerCase()} am I in?`,
  description: description.value,
  ogDescription: description.value,
});

// defineOgImage({
//   title: title.value,
//   subtitle: imageSubtitle.value,
// });
</script>
